import React, { useState } from "react";
import { Alert, Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, ModalDialog, ModalOverflow, Stack, Tooltip, Typography } from "@mui/joy";
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import { formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "./objects/ObjectPreview";
import { FormProvider, useForm } from "react-hook-form";
import { FieldEditTable } from "./FieldEditTable";
import { useUpdateTask } from "@api/taskService";
import toast from "react-hot-toast";
import { queryClient } from "@api/queryClient";
import { authenticatedApi } from "api";
import { useAuth } from "@providers/AuthProvider";
import { useLocation } from "react-router-dom";
import { useCreateDonationEntry, useUpdateDonationEntry } from "@api/donationEntryService";

const fields = [
    {
        name: 'amount',
        label: 'Amount',
        type: 'number',
        placeholder: 'e.g. 100',
        config: {
            required: true,
        },
    },
    {
        name: 'invoice_num',
        label: 'Invoice',
        type: 'text',
        placeholder: 'e.g. 54896',
    },
    {
        name: 'received',
        label: 'Date Received',
        type: 'date',
        placeholder: 'e.g. 05/23/2016',
    },
];

export function RecordDonationModal({
    entry = null,
    donation = null,
    onAfterSubmit = null,
    children,
}) {
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const { hash, pathname, search } = useLocation();

    let defaultValues = {};

    if(entry) {
        defaultValues = {...entry};
    } else if(donation) {
        defaultValues.donation_id = donation.id;
    }

    const formMethods = useForm({
        defaultValues,
    });
    const {
          setValue,
          reset,
          handleSubmit,
          watch,
          getValues,
          formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    const createEntry = useCreateDonationEntry(donation?.id);
    const updateEntry = useUpdateDonationEntry({parentId: donation?.id, id: entry?.id ?? 0});

    // check for dashboard vs contact vs work plan
    const performQueryKeyInvalidations = () => {
        queryClient.invalidateQueries({ queryKey: ['donations'] });
    };

    const onRelease = () => {
        const performUpdate = updateEntry.mutateAsync({
            received: null
        });
        toast.promise(performUpdate, {
            loading: `Updating...`,
            success: `The donation was released.`,
            error: (err) => `There was an error while saving.`,
        });
        performUpdate.then(() => {
            performQueryKeyInvalidations();
            reset();
            setOpen(false);
        });
    }

    const onSubmit = (fields) => {
        console.log(fields);
        const mutation = entry?.id ? updateEntry : createEntry;
        if(!entry?.id) {
            fields.donation_date = new Date();
        }
        const performMutation = mutation.mutateAsync({
            ...fields,
        });

        if(!fields.time_taken) {
            toast.promise(performMutation, {
                loading: `Adding...`,
                success: `The donation was recorded!`,
                error: (err) => `There was an error while saving.`,
            });
        }

        performMutation.then(() => {
            performQueryKeyInvalidations();
            reset();
            setOpen(false);
        });

    };

    return (<>
    {children(open, setOpen)}
    <Modal open={open} onClose={() => setOpen(false)}>
        <ModalOverflow>
        <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
            <ModalDialog sx={{width: '100%', maxWidth: '600px'}}>
                <DialogTitle>
                    {entry ? 'Edit Donation Entry' : 'Add Donation Entry'}
                </DialogTitle>
                <Divider/>

                {entry && entry.received && (
                    <Alert
                        variant="soft"
                        color="neutral"
                        endDecorator={
                            <Button size="sm" color="neutral" onClick={onRelease}>Release</Button>
                        }
                    >
                        <Typography><b>Note:</b> This entry has been received. If this was a mistake, you can release it.</Typography>
                    </Alert>
                )}

                <Box sx={{
                    marginInline: 'var(--Divider-inset)'
                }}
                >
                    <FieldEditTable mode="column" fields={fields}/>
                </Box>

                {/*
                {entry && !entry.received && (
                    <Checkbox
                        name="mark_completed"
                        {...formMethods.register('mark_completed')}
                        label={<Stack>
                            <Typography>Mark task as complete</Typography>
                            <Typography fontSize={'sm'}>You will still have the ability to add time to completed tasks.</Typography>
                        </Stack>}
                    />
                )}
                */}

                <DialogActions>
                    <Button
                        disabled={!isValid}
                        type="button" // this can't be submit type or it will submit the parent form
                        onClick={() => {
                        onSubmit(getValues());
                        }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                            setOpen(false);
                            try {
                                // onCancel();
                            } catch(e) {
                                //
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
      </FormProvider>
      </form>
      </ModalOverflow>
    </Modal>
    </>)
}
